import React, { useEffect, useState } from "react";
import { Storage, API, graphqlOperation, Auth } from "aws-amplify";
import { useParams } from "react-router-dom";
import Lottie from "lottie-react";
import brainAnimation from "../images/brain-lottie.json";
import { getTeacher, listCategories, listVideoURLs } from "../graphql/queries";

import "./VideoGrid.scss";
import Thumbnail from "./Thumbnail";
import VideoPlayer from "../pages/VideoPlayer";
import VideoPlayerData from "./VideoPlayerData";
import AccessDeniedModal from "./AccessDeniedModal";
import useFavorites from "../FavoritesUtils";

const VideoGrid = ({
    videos,
    filters,
    videoId,
    onFavoriteToggle,
    selectedTags,
    videoTags,
    tags,
    isOpen,
    instructors,
    favorites,
    toggleFavorites,
    isJourneyModalOpen,
    searchQuery
}) => {
    const [filteredVideos, setFilteredVideos] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isPlayerVisible, setPlayerVisible] = useState(false);
    const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
    const [noMatchingVideos, setNoMatchingVideos] = useState(false);
    const [videoURLsData, setVideoURLsData] = useState([]);
    const [isLoadingVideos, setIsLoadingVideos] = useState(true);
    const [isLoadingVideoURLs, setIsLoadingVideoURLs] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const getVideoDurationInMinutes = (duration) => {
        return parseInt(duration, 10);
    };

    // Check if only class level tags are selected
    const isOnlyClassLevelTagsSelected = () => {
        if (selectedTags.length === 0) return false;
        
        const classLevelTags = ["All Levels", "Intermediate", "Advanced"];
        const selectedTagNames = selectedTags.map(tag => tag.name);
        
        // Check if all selected tags are class level tags
        return selectedTagNames.every(tagName => classLevelTags.includes(tagName));
    };

    useEffect(() => {
        const fetchAllVideoURLs = async () => {
            setIsLoadingVideoURLs(true);
            let allItems = [];
            let nextToken = null;
            
            try {
                do {
                    const response = await API.graphql(
                        graphqlOperation(listVideoURLs, { 
                            limit: 10000,
                            nextToken: nextToken
                        })
                    );
                    
                    const items = response.data.listVideoURLs.items;
                    nextToken = response.data.listVideoURLs.nextToken;
                    allItems = [...allItems, ...items];
                    
                } while (nextToken);

                setVideoURLsData(allItems);
            } catch (error) {
                console.error("VideoGrid - Error fetching video URLs:", error);
                setVideoURLsData([]);
            } finally {
                setIsLoadingVideoURLs(false);
            }
        };

        fetchAllVideoURLs();
    }, []);

    useEffect(() => {
        if (videoId && videos.length > 0 && !isLoadingVideos && !isLoadingVideoURLs) {
            console.log("Starting video load sequence...");
            const video = videos.find(v => v.id === videoId);
            
            if (video) {
                console.log("Found video, attempting to open:", video.title);
                setTimeout(() => {
                    handleVideoSelect(video);
                }, 100);
            }
        }
    }, [videoId, videos, isLoadingVideos, isLoadingVideoURLs]);

    useEffect(() => {
        let isMounted = true;

        const initializePlayer = async () => {
            if (selectedVideo && !isPlayerVisible && isMounted) {
                console.log("Restoring player visibility");
                setPlayerVisible(true);
                setInitialLoadComplete(true);
            }
        };

        initializePlayer();

        return () => {
            isMounted = false;
        };
    }, [selectedVideo, isPlayerVisible]);

    useEffect(() => {
        let isMounted = true;

        const fetchVideoData = async () => {
            const categoriesResponse = await API.graphql(
                graphqlOperation(listCategories)
            );
            const categories = categoriesResponse.data.listCategories.items;
            const categoryIds = categories.map((category) => category.id);

            const filteredVideos = videos.filter((video) =>
                categoryIds.includes(video.categoryVideosId)
            );

            const videosWithImageUrls = await Promise.all(
                filteredVideos.map(async (video) => {
                    try {
                        const imageUrl = await Storage.get(video.poster, {
                            level: "public",
                        });
                        const instructor = instructors.find(
                            (instructor) =>
                                instructor.id === video.teacherVideosId
                        );
                        
                        let instructorImage = "";
                        if (instructor && instructor.image) {
                            instructorImage = await fetchInstructorImage(instructor.image);
                        }
                        //const instructorImage = await fetchInstructorImage(
                          //  instructor.image
                        //);
                        const category = categories.find(
                            (c) => c.id === video.categoryVideosId
                        );
                        const categoryName = category ? category.name : "";
                        // Get tags for this video
                        const videoTagIds = videoTags && Array.isArray(videoTags)
                            ? videoTags
                                .filter((videoTag) => videoTag.videoId === video.id)
                                .map((videoTag) => videoTag.tagId)
                            : [];
                        
                        const videoTagNames = tags && Array.isArray(tags)
                            ? tags
                                .filter((tag) => videoTagIds.includes(tag.id))
                                .map((tag) => tag.name)
                            : [];

                        const videoWithImageUrl = {
                            ...video,
                            imageUrl,
                            instructor,
                            instructorImage,
                            category: categoryName,
                            tags: videoTagNames,
                        };
                        return videoWithImageUrl;
                    } catch (error) {
                        console.log(
                            "Error fetching image or instructor data:",
                            error
                        );
                        return { ...video, imageUrl: "", instructor: null };
                    }
                })
            );

            let filteredVideosByFilters = videosWithImageUrls.filter(
                (video) => {
                    // Filter by categories
                    if (
                        filters.category.length > 0 &&
                        !filters.category.includes(video.categoryVideosId)
                    ) {
                        return false;
                    }

                    // Filter by tags
                    if (selectedTags.length > 0) {
                        const videoTagsIds = videoTags
                            .filter((tag) => tag.videoId === video.id)
                            .map((tag) => tag.tagId);

                        if (
                            !selectedTags.every((tag) =>
                                videoTagsIds.includes(tag.id)
                            )
                        ) {
                            return false;
                        }
                    }

                    // Filter by duration
                    if (filters.duration.length > 0) {
                        const videoDuration = getVideoDurationInMinutes(
                            video.duration
                        );

                        if (
                            !filters.duration.some((selectedDuration) => {
                                if (
                                    selectedDuration === "<15" &&
                                    videoDuration < 15
                                ) {
                                    return true;
                                }
                                if (
                                    selectedDuration === "15-30" &&
                                    videoDuration >= 15 &&
                                    videoDuration <= 30
                                ) {
                                    return true;
                                }
                                if (
                                    selectedDuration === "30-60" &&
                                    videoDuration >= 30 &&
                                    videoDuration <= 60
                                ) {
                                    return true;
                                }
                                if (
                                    selectedDuration === ">60" &&
                                    videoDuration > 60
                                ) {
                                    return true;
                                }
                                return false;
                            })
                        ) {
                            return false;
                        }
                    }

                    // Filter by teachers
                    if (filters.teacher.length > 0) {
                        const videoTeacherIds =
                            video.teacherVideosId.split(",");
                        if (
                            !filters.teacher.some((teacherId) =>
                                videoTeacherIds.includes(teacherId)
                            )
                        ) {
                            return false;
                        }
                    }
                    if (filters.favorites && !favorites.includes(video.id)) {
                        return false;
                    }

                    // Search filter - applied last (using prefix word matching)
                    if (searchQuery && searchQuery.trim()) {
                        const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/);
                        
                        // Build searchable text from video fields and split into words
                        const searchableText = [
                            video.title || '',
                            video.description || '',
                            video.category || '',
                            ...(video.tags || []),
                            video.instructor?.name || ''
                        ].join(' ').toLowerCase();
                        
                        // Split into words for prefix matching
                        const searchableWords = searchableText.split(/\s+/);
                        
                        // All search terms must match a word that STARTS WITH the term (prefix match)
                        if (!searchTerms.every(term => 
                            searchableWords.some(word => word.startsWith(term))
                        )) {
                            return false;
                        }
                    }

                    return true;
                }
            );

            //filter in descending order
            filteredVideosByFilters.sort((a, b) => {
                if (a.date > b.date) return -1; // descending order
                if (a.date < b.date) return 1;
                return 0;
            });

            // When search is active, prioritize title matches (using prefix word matching)
            if (searchQuery && searchQuery.trim()) {
                const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/);
                
                // Helper function to check if title has prefix word matches
                const titleHasPrefixMatch = (video) => {
                    const title = (video.title || '').toLowerCase();
                    const titleWords = title.split(/\s+/);
                    return searchTerms.every(term => 
                        titleWords.some(word => word.startsWith(term))
                    );
                };
                
                // Partition into title matches and other matches
                const titleMatches = filteredVideosByFilters.filter(titleHasPrefixMatch);
                const otherMatches = filteredVideosByFilters.filter(video => !titleHasPrefixMatch(video));
                
                // Combine: title matches first, then other matches (both already date-sorted)
                filteredVideosByFilters = [...titleMatches, ...otherMatches];
            }

            //setFilteredVideos(filteredVideosByFilters);

            // Update the state to indicate if there are no matching videos
            //setNoMatchingVideos(filteredVideosByFilters.length === 0);

            if (isMounted) {
                setFilteredVideos(filteredVideosByFilters);
                setNoMatchingVideos(filteredVideosByFilters.length === 0);
                setInitialLoadComplete(true);
            }

        };

        fetchVideoData().then(() => {
            setInitialLoadComplete(true);
        });

        return () => {
            isMounted = false;
        };

    }, [videos, filters, selectedTags, videoTags, tags, instructors, searchQuery]);

    const fetchInstructorImage = async (image) => {
        try {
            const imageUrl = await Storage.get(image, { level: "public" });
            setIsLoadingVideos(false);
            return imageUrl;
        } catch (error) {
            console.log("Error fetching instructor image:", error);
            return "";
        }
    };
    /*
    const fetchSubtitleUrl = async (video) => {
        try {
            const videoSplit = video.url.split("/")[1];
            const subtitleUrl = await Storage.get(
                "Subtitles/" + videoSplit + ".vtt",
                { level: "public" }
            );

            return subtitleUrl;
        } catch (error) {
            console.log("Error fetching subtitle:", error);
            return "";
        }
    };
    */
    const handleVideoSelect = async (video) => {
        try {
            const user = await Auth.currentAuthenticatedUser({
                bypassCache: true,
            });
            const token = user.signInUserSession.idToken.jwtToken;

            const myInit = {
                headers: {
                    Authorization: token,
                },
            };

            const response = await API.get(
                "bbwvideoauthapi",
                "/videoaccess",
                myInit
            );

            if (response !== "Access granted") {
                setShowAccessDeniedModal(true);
                return;
            }

            let videoUrl;
            let vttUrl = null;

            if (videoURLsData && Array.isArray(videoURLsData)) {
                const urlParts = video.url.split("/");
                const filenamePart = urlParts[urlParts.length - 1];
                // Remove 'Videos/' prefix if present
                const cleanVideoPath = video.url.replace(/^Videos\//, '');

                const matchedVideo = videoURLsData.find((item) => {
                    // Try matching MP4 field first
                    if (item.MP4) {
                        const itemUrlParts = item.MP4.split("/");
                        const itemFilenamePart = itemUrlParts[itemUrlParts.length - 1];
                        if (itemFilenamePart === filenamePart) return true;
                    }
                    
                    // Try matching srcvideo field if no MP4 match
                    if (item.srcvideo) {
                        // Remove 'Videos/' prefix if present in srcvideo
                        const cleanSrcVideo = item.srcvideo.replace(/^Videos\//, '');
                        if (cleanSrcVideo === cleanVideoPath) return true;
                    }
                    
                    return false;
                });

                if (matchedVideo) {
                    videoUrl = matchedVideo.HLS || matchedVideo.MP4;
                    vttUrl = matchedVideo.MP4 ? `${matchedVideo.MP4}.vtt` : null;
                } else {
                    videoUrl = await Storage.get(video.url, { level: "public" });
                }
            } else {
                videoUrl = await Storage.get(video.url, { level: "public" });
            }

            const imageUrl = await Storage.get(video.poster, {
                level: "public",
            });

            const instructor = instructors.find(
                (instructor) => instructor.id === video.teacherVideosId
            );
            const instructorImage = await fetchInstructorImage(
                instructor.image
            );

            const selectedVideoWithUrls = {
                ...video,
                url: videoUrl,
                imageUrl: imageUrl,
                captions: vttUrl,
                instructor: {
                    ...instructor,
                    image: instructorImage,
                },
            };

            setSelectedVideo(selectedVideoWithUrls);
            setPlayerVisible(true);
            setInitialLoadComplete(true);
            const playUrl = `${window.location.origin}/play/${video.id}`;
            window.history.pushState({}, "", playUrl);
        } catch (error) {
            console.error("VideoGrid - Error in handleVideoSelect:", error);
        } finally {
            setIsLoadingVideos(false);
            setInitialLoadComplete(true);
        }
    };

    useEffect(() => {
        const selectedVideo = videos.find((video) => video.id === videoId);
        if (selectedVideo) {
            handleVideoSelect(selectedVideo);
        } else {
            setSelectedVideo(null);
            setPlayerVisible(false);
        }
    }, [videos, videoId]);

    const closeVideoPlayer = () => {
        setPlayerVisible(false);
        setSelectedVideo(null);
        localStorage.removeItem("tempUUID");
        const newUrl = window.location.origin;
        window.history.pushState({}, "", newUrl);
    };

    const handleFavoriteClick = (videoId, isFavorite) => {
        onFavoriteToggle(videoId, !isFavorite);
    };

    return (
        <>
            <div 
                className={`video-grid-container ${isOpen ? 'sidebar-open' : ''} ${isPlayerVisible ? 'modal-open' : ''}`}
            >
                {showAccessDeniedModal && (
                    <AccessDeniedModal
                        onClose={() => setShowAccessDeniedModal(false)}
                    />
                )}
                {(isLoadingVideos || isLoadingVideoURLs) && (
                    <div className="loading-videos">
                        Loading videos...
                        <Lottie
                            animationData={brainAnimation}
                            style={{
                                width: 200,
                                height: 200,
                            }}
                        />
                    </div> // This is the placeholder/loader
                )}
                {!isLoadingVideos &&
                    !isLoadingVideoURLs &&
                    initialLoadComplete && (
                        <>
                            {noMatchingVideos &&
                                filteredVideos.length === 0 && (
                                    <div className="no-matching-videos-message">
                                        {isOnlyClassLevelTagsSelected() ? (
                                            <>
                                               Coming Soon!
                                            </>
                                        ) : (
                                            <>
                                                Sorry! There are no videos with this
                                                specific combination of tags. <br></br>
                                                To get videos to populate, remove a few
                                                of the tags. We love to create
                                                programming around your needs! If you
                                                would like a class to focus on this
                                                specific group of topics, please email
                                                support@brainbased-wellness.com and let
                                                us know what you want a class on. We
                                                will create it for you soon!
                                            </>
                                        )}
                                    </div>
                                )}

                            {filteredVideos.length > 0 && (
                                <div className="video-grid">
                                    {filteredVideos.map((video, index) => (
                                        <Thumbnail
                                            key={index}
                                            video={video}
                                            title={video.title}
                                            category={video.category}
                                            date={video.date}
                                            duration={video.duration}
                                            image={video.imageUrl}
                                            instructor={
                                                video.instructor
                                                    ? video.instructor.name
                                                    : ""
                                            }
                                            instructorImage={
                                                video.instructorImage
                                            }
                                            tags={video.tags}
                                            favorites={favorites}
                                            toggleFavorites={toggleFavorites}
                                            isFavorite={favorites.includes(
                                                video.id
                                            )}
                                            onFavoriteToggle={
                                                handleFavoriteClick
                                            }
                                            onClick={() =>
                                                handleVideoSelect(video)
                                            }
                                        />
                                    ))}
                                </div>
                            )}

                            {selectedVideo && initialLoadComplete && (
                                <VideoPlayerData
                                    video={selectedVideo}
                                    instructor={selectedVideo.instructor?.name || ""}
                                    isFavorite={favorites.includes(selectedVideo.id)}
                                    favorites={favorites}
                                    onFavoriteToggle={handleFavoriteClick}
                                    isOpen={isPlayerVisible}
                                    onClose={closeVideoPlayer}
                                >
                                    <VideoPlayer
                                        videoUrl={selectedVideo.url}
                                        poster={selectedVideo.imageUrl}
                                        captions={selectedVideo.captions}
                                    />
                                </VideoPlayerData>
                            )}
                        </>
                    )}
            </div>
        </>
    );
};

export default VideoGrid;